import React, { useEffect, useRef, useState } from "react";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  ControlBar,
  ParticipantTile,
  useTracks,
  useParticipants,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import "@livekit/components-styles";
import { api } from "@/lib/api";
import * as log from "@/lib/logger";
import { X, Minimize2, Maximize2, PhoneOff, Mic, MicOff, Video, VideoOff } from "lucide-react";
import { toast } from "sonner";
import HuddlePreflight from "@/components/HuddlePreflight";

/**
 * Full WebRTC call overlay powered by LiveKit Cloud with:
 *  - Pre-flight device check (HuddlePreflight)
 *  - Minimize into a floating call bar so the user can navigate the app
 *    while staying connected
 *  - Smart grid layout for 1 / 2 / 3-4 / 5-6 / 7+ participants
 *  - Speaker pulse on the active-speaker tile
 */
export default function CallOverlay({ call, onClose }) {
  const [token, setToken] = useState("");
  const [serverUrl, setServerUrl] = useState("");
  const [connectError, setConnectError] = useState("");
  const [prefs, setPrefs] = useState(null); // set after pre-flight
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    if (!prefs) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/livekit/token", { room: call.room });
        if (cancelled) return;
        setToken(data.token);
        setServerUrl(data.url);
      } catch (err) {
        log.debug("[call] token error", err);
        setConnectError(
          err?.response?.data?.detail || "Could not connect to the call service"
        );
      }
    })();
    return () => { cancelled = true; };
  }, [call.room, prefs]);

  const leave = async () => {
    try { await api.post(`/calls/${call.id}/leave`); }
    catch (err) { log.debug("[call] leave error", err); }
    onClose();
  };

  // Show pre-flight first; only after user commits to Join do we mount LiveKit.
  if (!prefs) {
    return (
      <HuddlePreflight
        kind={call.kind}
        onJoin={setPrefs}
        onCancel={leave}
      />
    );
  }

  return (
    <div
      className={
        minimized
          ? "fixed bottom-4 right-4 z-50" // floating bar
          : "fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      }
      data-testid="call-overlay"
      data-minimized={minimized ? "true" : "false"}
    >
      <div
        className={
          minimized
            ? "bg-zinc-950 text-white rounded-full shadow-2xl border border-white/10 flex items-center gap-2 pl-3 pr-2 py-2"
            : "bg-zinc-950 text-white rounded-2xl w-[880px] max-w-[94vw] h-[80vh] max-h-[720px] overflow-hidden shadow-2xl flex flex-col"
        }
      >
        {!minimized && (
          <div className="p-3 flex items-center justify-between border-b border-white/10 shrink-0">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400">
                {call.kind === "video" ? "Video room" : "Voice room"}
              </div>
              <div className="font-semibold text-sm truncate">{call.room}</div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setMinimized(true)}
                className="p-1.5 rounded hover:bg-white/10"
                data-testid="call-minimize"
                aria-label="Minimize call"
                title="Minimize (stay in call while you browse)"
              >
                <Minimize2 className="w-4 h-4" />
              </button>
              <button
                onClick={leave}
                className="p-1.5 rounded hover:bg-white/10"
                data-testid="call-close"
                aria-label="Leave call"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {connectError ? (
          minimized ? null : <ConnectError message={connectError} onLeave={leave} />
        ) : !token ? (
          minimized ? null : (
            <div className="flex-1 flex items-center justify-center text-sm text-zinc-400" data-testid="call-connecting">
              Connecting…
            </div>
          )
        ) : (
          <LiveKitRoom
            token={token}
            serverUrl={serverUrl}
            connect={true}
            audio={prefs.audio ? (prefs.audioDeviceId ? { deviceId: prefs.audioDeviceId } : true) : false}
            video={prefs.video ? (prefs.videoDeviceId ? { deviceId: prefs.videoDeviceId } : true) : false}
            data-lk-theme="default"
            className={minimized ? "contents" : "w-full flex-1 min-h-0 flex flex-col"}
            onDisconnected={leave}
            onError={(e) => {
              log.debug("[call] room error", e);
              toast.error("Call error — leaving room");
              setTimeout(leave, 400);
            }}
          >
            {minimized ? (
              <MinimizedBar onExpand={() => setMinimized(false)} onLeave={leave} kind={call.kind} room={call.room}/>
            ) : (
              <>
                <div className="flex-1 min-h-0 bg-black flex items-center justify-center">
                  <CallStage kind={call.kind} />
                </div>
                <RoomAudioRenderer />
                <div className="border-t border-white/10">
                  <ControlBar
                    controls={{
                      microphone: true,
                      camera: call.kind === "video",
                      screenShare: true,
                      chat: false,
                      leave: true,
                    }}
                  />
                </div>
              </>
            )}
            {/* Audio renderer must always be mounted, even while minimized,
                otherwise voices drop the moment we hide the video grid. */}
            {minimized && <RoomAudioRenderer />}
          </LiveKitRoom>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Minimized bar shown when the user clicks minimize. Renders mic/cam toggle
// and expand + leave. Read participant + local mic/cam state from LiveKit.
// -----------------------------------------------------------------------------
function MinimizedBar({ onExpand, onLeave, kind, room }) {
  const participants = useParticipants();
  const local = participants.find((p) => p.isLocal);
  const [audioOn, setAudioOn] = useState(true);
  const [videoOn, setVideoOn] = useState(kind === "video");

  const toggleMic = async () => {
    if (!local) return;
    await local.setMicrophoneEnabled(!audioOn);
    setAudioOn((v) => !v);
  };
  const toggleCam = async () => {
    if (!local) return;
    await local.setCameraEnabled(!videoOn);
    setVideoOn((v) => !v);
  };

  const speaker = participants.find((p) => p.isSpeaking);
  return (
    <div className="flex items-center gap-2" data-testid="call-floating-bar">
      <div className="flex items-center gap-1.5 pl-1 pr-2">
        <span className={`w-2 h-2 rounded-full ${speaker ? "bg-emerald-400 animate-pulse" : "bg-zinc-500"}`}/>
        <div className="text-xs">
          <div className="font-semibold leading-tight truncate max-w-[140px]">
            {speaker?.name || speaker?.identity || room}
          </div>
          <div className="text-[10px] text-zinc-400 leading-tight">
            {participants.length} in call
          </div>
        </div>
      </div>
      <button
        onClick={toggleMic}
        data-testid="call-floating-mic"
        aria-label={audioOn ? "Mute microphone" : "Unmute microphone"}
        className={`w-8 h-8 rounded-full flex items-center justify-center ${audioOn ? "bg-white/10 hover:bg-white/15" : "bg-red-500/40 hover:bg-red-500/50"}`}
      >
        {audioOn ? <Mic className="w-3.5 h-3.5"/> : <MicOff className="w-3.5 h-3.5"/>}
      </button>
      {kind === "video" && (
        <button
          onClick={toggleCam}
          data-testid="call-floating-cam"
          aria-label={videoOn ? "Turn camera off" : "Turn camera on"}
          className={`w-8 h-8 rounded-full flex items-center justify-center ${videoOn ? "bg-white/10 hover:bg-white/15" : "bg-red-500/40 hover:bg-red-500/50"}`}
        >
          {videoOn ? <Video className="w-3.5 h-3.5"/> : <VideoOff className="w-3.5 h-3.5"/>}
        </button>
      )}
      <button
        onClick={onExpand}
        data-testid="call-floating-expand"
        aria-label="Expand call"
        className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center"
      >
        <Maximize2 className="w-3.5 h-3.5"/>
      </button>
      <button
        onClick={onLeave}
        data-testid="call-floating-leave"
        aria-label="Leave call"
        className="w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center"
      >
        <PhoneOff className="w-3.5 h-3.5"/>
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Smart layout — chooses columns based on participant count and highlights
// the active speaker with a pulsing emerald border.
// -----------------------------------------------------------------------------
function CallStage({ kind }) {
  const cameraAndScreen = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );
  const screenTracks = cameraAndScreen.filter(
    (t) => t.publication && t.source === Track.Source.ScreenShare
  );

  if (kind !== "video" && screenTracks.length === 0) {
    return <VoiceRoster />;
  }
  const tracks = kind === "video" ? cameraAndScreen : screenTracks;
  const n = tracks.length;
  const cols =
    n <= 1 ? "grid-cols-1"
    : n === 2 ? "grid-cols-2"
    : n <= 4 ? "grid-cols-2"
    : n <= 9 ? "grid-cols-3"
    : "grid-cols-4";

  return (
    <div className={`w-full h-full grid ${cols} gap-2 p-2 auto-rows-fr`} data-testid={`call-grid-n${n}`}>
      {tracks.map((tr, i) => {
        const speaking = tr.participant?.isSpeaking;
        return (
          <div
            key={tr.participant?.identity + "-" + tr.source + "-" + i}
            className={`relative rounded-lg overflow-hidden bg-zinc-800 transition-shadow ${
              speaking ? "ring-2 ring-emerald-400 shadow-[0_0_0_4px_rgba(52,211,153,0.15)]" : ""
            }`}
          >
            <ParticipantTile trackRef={tr} className="w-full h-full"/>
          </div>
        );
      })}
    </div>
  );
}

function VoiceRoster() {
  const tracks = useTracks(
    [{ source: Track.Source.Microphone, withPlaceholder: true }],
    { onlySubscribed: false }
  );
  return (
    <div className="w-full h-full flex flex-wrap items-center justify-center gap-6 p-6">
      {tracks.map((trackRef, i) => {
        const p = trackRef.participant;
        const identity = p.identity || p.name || `guest-${i}`;
        const initials = (p.name || identity).slice(0, 2).toUpperCase();
        const speaking = p.isSpeaking;
        return (
          <div
            key={identity}
            className="flex flex-col items-center gap-2"
            data-testid={`voice-tile-${identity}`}
          >
            <div
              className={`w-24 h-24 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-white text-3xl font-bold flex items-center justify-center transition-all ${
                speaking ? "ring-4 ring-emerald-400 scale-105 shadow-[0_0_0_10px_rgba(52,211,153,0.15)]" : "ring-2 ring-white/10"
              }`}
            >
              {initials}
            </div>
            <div className="text-sm font-medium">{p.name || identity}</div>
            <div className="text-[10px] text-zinc-400">
              {speaking ? "speaking…" : p.isLocal ? "you" : "connected"}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ConnectError({ message, onLeave }) {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center px-6" data-testid="call-error">
        <div className="text-lg font-semibold">Could not connect</div>
        <div className="mt-2 text-sm text-zinc-400">{message}</div>
        <button
          onClick={onLeave}
          className="mt-4 h-9 px-4 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-600"
        >Close</button>
      </div>
    </div>
  );
}
